import { useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '/business/shared/state/store';

import { setPreferencePayload, getCookiePreferences } from '/business/shared/functions/cookiePreferences';

import LinkButton from '/shared/components/widgets/button/LinkButton';
import Button from '/shared/components/widgets/button/Button';

import StyledCookieWarning from './__styled__/StyledCookieWarning';

import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

type CookieWarningProps = {
  cookieUrl?: string;
  cookieUrlIsHref?: boolean;
};

function CookieWarning({ cookieUrl = '/cookie-preferences', cookieUrlIsHref = false }: CookieWarningProps) {
  const modal = useSelector((state: RootState) => state.modal?.modal) || null;

  useTranslation();
  const location = useLocation();

  const cookiePreferences = getCookiePreferences();

  const [dismissed, setDismissed] = useState(false);

  const isCookiePrefPage = location.pathname.endsWith('cookie-preferences');
  const displayCookieBar = !dismissed && !isCookiePrefPage;

  if (!displayCookieBar || cookiePreferences.confirmed) {
    return null;
  }

  const setAgreeAll = () => {
    setDismissed(true);
    setPreferencePayload({
      hotjar: true,
      performance: true,
    });
  };

  let cookieUrlHref: string | undefined;
  let cookieUrlTo: string | undefined;
  if (cookieUrlIsHref === true) {
    cookieUrlHref = cookieUrl;
  } else {
    cookieUrlTo = cookieUrl;
  }

  if (modal !== null) {
    // If there is a modal, just HIDE the cookie banner, as it is blocking.
    // The user must have performed an action for this to have occured
    return null;
  }
  return (
    <StyledCookieWarning className="carbon-calculator-cookie-warning">
      <div>
        <div>
          <StyledHeading $green as="h3" $type="h3">
            {/* @i18n: Site traffic information and cookies */}
            <Trans i18nKey="business:SiteComponents.cookieWarning.heading" />
          </StyledHeading>
          <StyledParagraph $smallText>
            {/* @i18n: We use cookies to collect and analyse information on our site's performance and to[...] */}
            <Trans i18nKey="business:SiteComponents.cookieWarning.para" />
          </StyledParagraph>
        </div>
        <div>
          <LinkButton $secondary $inline href={cookieUrlHref} to={cookieUrlTo}>
            {/* @i18n: Manage cookie preferences */}
            <Trans i18nKey="business:Buttons.manageCookiePreferences" />
          </LinkButton>
          <Button $primary onClick={() => setAgreeAll()}>
            {/* @i18n: Allow */}
            <Trans i18nKey="business:Buttons.allow" />
          </Button>
        </div>
      </div>
    </StyledCookieWarning>
  );
}

export default CookieWarning;
